<template>
  <dashboard-layout-template
    :showQuickSetup="showQuickSetup"
    :showTransitionModal="showTransitionModal"
    :transitionModalFadeIn="true"
    :transitionModalFadeOut="true"
    :showSidePanel="true"
  >
    <template slot="header">
      <div class="setup-header">{{$t('Header')}}<span class="userName"> {{AccountWelcomeName}}!</span></div>
      <div class="setup-subheader">{{$t('SubHeader')}}</div>
    </template>
    <template slot="notifications">
      <InfoMessageBannerPanel v-if="showMerchantCareMessage" :panelSize="'large'" :loading="initializing" :dismissMessage="dismissMessage"/>
    </template>
    <template slot="panel" v-if="!showNewChecklistUI">
        <GetStartedSetupPanel :panelSize="'large'" :loading="initializing"/>
    </template>
    <template slot="panel" v-else>
        <GetStartedSetupPanelV2 :panelSize="'large'" :loading="initializing"/>
    </template>
    <template slot="sidepanel">
        <GetStartedToolsPanel :panelSize="'large'" :loading="initializing" :showCostCalculatorModal="costCalculatorModalToggle"/>
        <WelcomeSetupPanel :panelSize="'large'" :loading="initializing"/>
    </template>
    <template slot="modal">
        <CostCalculatorModal :showModal="showCostCalculatorModal" :onClose="costCalculatorModalToggle" />
    </template>
  </dashboard-layout-template>
</template>

<script>
import DashboardLayoutTemplate from '@/components/layouts/DashboardLayoutTemplate'
import WelcomeSetupPanel from '@/components/dashboard/panels/WelcomeSetupPanel'
import GetStartedToolsPanel from '@/components/dashboard/panels/GetStartedToolsPanel'
import GetStartedSetupPanel from '@/components/dashboard/panels/GetStartedSetupPanel'
import GetStartedSetupPanelV2 from '@/components/dashboard/panels/GetStartedSetupPanelV2'
import InfoMessageBannerPanel from '@/components/dashboard/panels/InfoMessageBannerPanel'
import { SetupGuideEnum } from '@/enums/modules/routes'
import BaseRoutesEnum from '@/enums/modules/routes/base'
import { ThirdPartyToolsManager } from '@/utils/thirdPartyToolsManager'
import SignupTrackingScripts from '../../mixins/setupGuideSignupScripts'
import CostCalculatorModal from './modals/CostCalculatorModal.vue'

export default {
  name: 'SetupDashboard',
  mixins: [SignupTrackingScripts],
  components: {
    WelcomeSetupPanel,
    GetStartedToolsPanel,
    GetStartedSetupPanel,
    GetStartedSetupPanelV2,
    InfoMessageBannerPanel,
    DashboardLayoutTemplate,
    CostCalculatorModal
  },
  data () {
    return {
      // maybe we should have some initialization flag sent to the layout, and have it hide things until we tell it otherwise?
      initializing: true,
      showTransitionModal: false,
      showQuickSetup: true,
      showMerchantCareMessage: false,
      showCostCalculatorModal: false
    }
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      await vm.$shipBobStore.dispatch('dashboard/getDashboardSettings')
      await vm.initialize()
    })
  },
  destroyed () {
    if (this.$route.name !== BaseRoutesEnum.DashboardV2) {
      this.$shipBobStore.unregisterModule('dashboard')
    }
    this.$shipBobStore.unregisterModule('setupDashboard')
    this.$shipBobStore.unregisterModule(['user', 'qualifier'])
  },
  mounted () {
    var cookie = this.$cookies.get('signup')
    if (cookie === 'true') {
      this.runSignupTrackingScripts()
      this.$cookies.remove('signup')
    }
    this.googleAdwordsConversionScript()
    ThirdPartyToolsManager.enablePendoGuides()
  },
  computed: {
    dashboardSettings () {
      return this.$shipBobStore.getters['dashboard/dashboardSettings']
    },
    accountManagerSettings () {
      return this.$shipBobStore.getters['accountManager/accountManager']
    },
    pricePlanSettings () {
      return this.$shipBobStore.getters['setupDashboard/pricingPlanSetupPanelModel']
    },
    showNewChecklistUI () {
      return this.$shipBobStore.getters['getStartedStepChecklistNewUI']
    },
    quoteGeneratorUser () {
      return this.dashboardSettings.ShowQuoteGenerator
    },
    hasCompletedQuoteGenerator () {
      return this.dashboardSettings.HasCompletedQuoteGenerator
    },
    hasAccountManager () {
      return this.accountManagerSettings.UserId > 0
    },
    hasPricePlan () {
      return (this.pricePlanSettings.PricingPlan > 0)
    },
    showGetStartedSetupPanel () {
      return this.hasCompletedQuoteGenerator && (this.hasPricePlan || this.hasAccountManager)
    },
    onboardingComplete () {
      return this.dashboardSettings.OnboardingComplete
    },
    showIntercom () {
      return this.dashboardSettings.ShowIntercom
    },
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    loggedInUser () {
      return this.$shipBobStore.getters['user/getLoggedInUser']
    },
    impersonatedUser () {
      return this.$shipBobStore.getters['user/getImpersonatedUser']
    },
    dismissMessageMap () {
      const dismissMapString = window.localStorage.getItem('MerchantCareMessageDismiss')
      return dismissMapString ? JSON.parse(dismissMapString) : {}
    },
    onboardingWizardShown () {
      return this.$shipBobStore.getters['onboarding/onboardingShown']
    },
    isBdrGrowthSignup () {
      return this.$shipBobStore.getters['onboarding/shouldHideCostPage']
    },
    userName () {
      return this.user._firstName
    },
    AccountWelcomeName () {
      if (this.loggedInUser.userId.value === this.impersonatedUser.userId.value) {
        return this.loggedInUser.firstName.value
      } else if (!this.loggedInUser.isAdmin.value) {
        return this.loggedInUser.firstName.value
      } else {
        return this.impersonatedUser.firstName.value
      }
    }
  },
  methods: {
    async initialize () {
      if (this.onboardingComplete) {
        this.handleOnboardingComplete()
      } else {
        await this.setupHelp()
        this.initializing = false
      }

      const dismissMapping = this.dismissMessageMap
      this.showMerchantCareMessage = this.onboardingWizardShown && this.isBdrGrowthSignup && !dismissMapping[this.user.impersonatedUserId]
    },
    replaceUserName (text) {
      return text.replace('%UserName%', this.userName)
    },
    handleOnboardingComplete () {
      const user = this.$shipBobStore.getters['user/getUser']
      this.$shipBobStore.dispatch('user/setUserStep', user.getStepEnum(SetupGuideEnum.OnboardingComplete)).then((response) => {
        this.$router.push({ name: BaseRoutesEnum.Dashboard })
      })
    },
    submitQuoteGenerator () {
      this.showTransitionModal = true
    },
    pricingPlanPanelLoaded () {
      this.showTransitionModal = false
    },
    async setupHelp () {
      if (this.showIntercom) {
        ThirdPartyToolsManager.ZendeskWrapper.hideZendesk()
      }
    },
    dismissMessage () {
      this.showMerchantCareMessage = false
      const dismissMapping = this.dismissMessageMap
      dismissMapping[this.user.impersonatedUserId] = true
      window.localStorage.setItem('MerchantCareMessageDismiss', JSON.stringify(dismissMapping))
    },
    costCalculatorModalToggle () {
      this.showCostCalculatorModal = !this.showCostCalculatorModal
    }
  },
  i18n: {
    messages: {
      'en-US': {
        Header: 'Welcome,',
        SubHeader: 'Complete the steps below to start shipping with ShipBob.'
      }
    }
  }
}
</script>
<style scoped lang='scss'>
@import '../../_colors';
.mobile-panel {
  display: none;
}

.desktop-panel {
  display: initial;
}

@media all and (max-width: 1300px) {
  .mobile-panel {
    display: initial;
  }

  .desktop-panel {
    display: none;
  }
}

.setup-subheader {
  font-size: 1rem;
  line-height: 1.1875rem;
  color: $setup-page-title
}

.setup-header {
  font-size: 2.25rem;
  line-height: 2.75rem;
  font-weight: bold;
  color: $setup-page-title;
  span.userName{
    color:$setup-page-title-name;
  }
}
</style>
